import React, { useState, useEffect } from 'react';
import WebModal from './Modal';
import { useTranslation } from 'react-i18next';
import { Button } from 'aha-components';
import { isMobile } from '@/biz/tool';
import { useNavigate } from 'react-router-dom';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import axiosInstance from '@/apis/axiosInstance';
import { RightIcon } from '@/components/common/icon';
import { hasClerkUser } from '@/biz/tool';

interface Props {}

const ModalSoftDeleteTip: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { showModalSoftDeleteTip, softDeleteReason } = useWebModalStore();
  const [invitesList, setInvitesList] = useState<any[]>([]);

  const deletedReason = {
    platform_mismatch: t('modal.soft_delete.deleted_reason.platform_mismatch', {
      defaultValue: 'Platform'
    }),
    country_mismatch: t('modal.soft_delete.deleted_reason.country_mismatch', {
      defaultValue: 'Country'
    }),
    language_mismatch: t('modal.soft_delete.deleted_reason.language_mismatch', {
      defaultValue: 'Language'
    }),
    personas_mismatch: t('modal.soft_delete.deleted_reason.personas_mismatch', {
      defaultValue: 'Creator profile'
    }),
    placements_mismatch: t(
      'modal.soft_delete.deleted_reason.placements_mismatch',
      {
        defaultValue: 'Content type'
      }
    ),
    price_limit_mismatch: t(
      'modal.soft_delete.deleted_reason.price_limit_mismatch',
      {
        defaultValue: 'Budget'
      }
    ),
    cpm_limit_mismatch: t(
      'modal.soft_delete.deleted_reason.cpm_limit_mismatch',
      {
        defaultValue: 'Budget'
      }
    ),
    blocklist_mismatch: t(
      'modal.soft_delete.deleted_reason.blocklist_mismatch',
      {
        defaultValue: 'Creator profile'
      }
    ),
    pi_score_mismatch: t('modal.soft_delete.deleted_reason.pi_score_mismatch', {
      defaultValue: 'Creator profile'
    }),
    audience_mismatch: t('modal.soft_delete.deleted_reason.audience_mismatch', {
      defaultValue: 'Product audience'
    }),
    posting_frequency_mismatch: t(
      'modal.soft_delete.deleted_reason.posting_frequency_mismatch',
      {
        defaultValue: 'Creator account activeness'
      }
    ),
    last_post_time_mismatch: t(
      'modal.soft_delete.deleted_reason.last_post_time_mismatch',
      {
        defaultValue: 'Creator account activeness'
      }
    ),
    predicted_views_mismatch: t(
      'modal.soft_delete.deleted_reason.predicted_views_mismatch',
      {
        defaultValue: 'Creator account performance'
      }
    ),
    followers_mismatch: t(
      'modal.soft_delete.deleted_reason.followers_mismatch',
      {
        defaultValue: 'Creator follower number'
      }
    ),
    order_deleted: t('modal.soft_delete.deleted_reason.order_deleted', {
      defaultValue: 'Order deleted'
    }),
    wrong_added_creator: t(
      'modal.soft_delete.deleted_reason.wrong_added_creator',
      {
        defaultValue: 'Creator profile'
      }
    )
  };

  const getInvitesList = async () => {
    const { data } = await axiosInstance.get('/influencer/invites');
    if (data.code === 0) {
      setInvitesList(data?.data?.active?.slice(0, 10) || []);
    } else {
      setInvitesList([]);
    }
  };

  useEffect(() => {
    if (hasClerkUser()) {
      getInvitesList();
    }
  }, []);

  return (
    <>
      <WebModal
        className="bg-gradient-to-b from-[#EAE9E8] to-[#fff]"
        width={isMobile() ? 380 : 640}
        visible={showModalSoftDeleteTip}
        isShowHeader={false}
        isShowFooter={false}
      >
        {invitesList && invitesList.length > 0 ? (
          <ModalSoftDeleteContentDataMatch
            invitesList={invitesList || []}
            softDeleteReason={
              deletedReason[softDeleteReason as keyof typeof deletedReason] ||
              ''
            }
          />
        ) : (
          <ModalSoftDeleteContentNoData
            softDeleteReason={
              deletedReason[softDeleteReason as keyof typeof deletedReason] ||
              ''
            }
          />
        )}
      </WebModal>
    </>
  );
};

const ModalSoftDeleteContentNoData: React.FC<{ softDeleteReason: string }> = ({
  softDeleteReason
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setShowModalSoftDeleteTip, setSoftDeleteReason } = useWebModalStore();

  return (
    <>
      <div className="w-full max-h-[70vh] overflow-y-auto">
        <div className="flex justify-center">
          <img
            className="w-[248px]"
            src={'/modal-soft-delete-img.png'}
            alt="paused"
          />
        </div>
        <div>
          <div className="w-full text-center mb-3">
            <div className="text-center justify-start text-xl font-semibold leading-8">
              {t('modal.soft_delete.soft_delete_no_title', {
                defaultValue: 'No worries, we’ll find your next collaboration'
              })}
            </div>
          </div>
          <div className="text-base font-normal text-[#79716B] text-center felx flex-col px-4">
            <div className="">
              {t('modal.soft_delete.soft_delete_no_desc_1', {
                defaultValue:
                  'This collaboration is no longer available because the brand updated the campaign details.'
              })}
            </div>
            <div className="mt-4">
              {t('modal.soft_delete.soft_delete_no_desc_2', {
                defaultValue: 'Updated requirement: {{specificReason}}.',
                specificReason: softDeleteReason || '-'
              })}
            </div>
            <div className="mt-4">
              {t('modal.soft_delete.soft_delete_no_desc_3', {
                defaultValue:
                  'We’ll notify you as soon as new collaborations that match you go live!'
              })}
            </div>
          </div>
        </div>
        <div className="mt-8 flex items-end justify-end border-t border-[#E5E7EB] pt-4 gap-3">
          <Button
            className=""
            onClick={() => {
              setShowModalSoftDeleteTip(false);
              navigate('/');
              setSoftDeleteReason('');
            }}
          >
            {t('modal.soft_delete.back_to_dashboard', {
              defaultValue: 'Back to dashboard'
            })}
          </Button>
          <Button
            type="primary"
            className="!w-[120px]"
            onClick={() => {
              setShowModalSoftDeleteTip(false);
              setSoftDeleteReason('');
            }}
          >
            {t('modal.soft_delete.btn_no_text', {
              defaultValue: 'Got it'
            })}
          </Button>
        </div>
      </div>
    </>
  );
};

const ModalSoftDeleteContentDataMatch: React.FC<{
  softDeleteReason: string;
  invitesList: any[];
}> = ({ softDeleteReason, invitesList }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { setShowModalSoftDeleteTip, setSoftDeleteReason } = useWebModalStore();

  return (
    <>
      <div className="w-full overflow-y-auto">
        <div className="max-h-[calc(100vh-260px)] overflow-y-auto">
          <div className="flex justify-center">
            <img
              className="w-[248px]"
              src={'/modal-soft-delete-img.png'}
              alt=""
            />
          </div>
          <div className="mb-4">
            <div className="w-full text-center mb-3">
              <div className="text-center justify-start text-xl font-semibold leading-8">
                {t('modal.soft_delete.data_match.title', {
                  defaultValue: 'This collaboration is no longer available'
                })}
              </div>
            </div>
            <div className="text-base font-normal text-[#79716B] text-center felx flex-col px-4">
              <div className="">
                {t('modal.soft_delete.data_match.desc_1', {
                  defaultValue:
                    'The brand just updated the campaign details, so this collaboration is no longer available.'
                })}
              </div>
              <div className="mt-4">
                {t('modal.soft_delete.data_match.desc_2', {
                  defaultValue: 'Updated requirement: {{specificReason}}.',
                  specificReason: softDeleteReason || '-'
                })}
              </div>
              <div className="mt-4">
                {t('modal.soft_delete.data_match.desc_3', {
                  defaultValue:
                    'This has nothing to do with your account or performance. New collaborations are posted regularly. Take a look below and pick your next one!'
                })}
              </div>
            </div>
          </div>
          {invitesList && invitesList.length > 0 && (
            <div className="flex flex-wrap bg-[#fff] p-4 rounded-lg">
              {invitesList.map((item) => (
                <div
                  key={item.id}
                  className="w-full px-4 py-4 flex items-center justify-between border-b border-[#E5E7EB]"
                >
                  <div className="w-full flex items-center justify-between gap-4">
                    <div className="w-[64px] h-[64px] flex-shrink-0 rounded-xl bg-[#fff] p-1 border border-[#E5E7EB]">
                      <img
                        src={item?.campaign?.product?.logo}
                        alt="logo"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-full flex flex-col items-start justify-between gap-2">
                      <div className="">{item?.campaign?.product?.name}</div>
                      <a
                        className="text-xs text-[#0c67e6]"
                        href={item?.campaign?.product?.website}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item?.campaign?.product?.website}
                      </a>
                    </div>
                  </div>
                  <Button
                    className="mt-4 w-[160px]"
                    onClick={() => {
                      setShowModalSoftDeleteTip(false);
                      navigate(`/campaign/invites/detail/${item.id}`);
                    }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {t('modal.soft_delete.data_match.share_rate_btn_text', {
                        defaultValue: 'Share rate'
                      })}
                      <RightIcon className="w-[12px] h-[12px]" />
                    </div>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-[#fff] mt-8 flex items-end justify-end border-t border-[#E5E7EB] pt-4 gap-3">
          <Button
            className=""
            onClick={() => {
              setShowModalSoftDeleteTip(false);
              navigate('/');
              setSoftDeleteReason('');
            }}
          >
            {t('modal.soft_delete.back_to_dashboard', {
              defaultValue: 'Back to dashboard'
            })}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setShowModalSoftDeleteTip(false);
              navigate('/campaign/invites');
              setSoftDeleteReason('');
            }}
          >
            {t('modal.soft_delete.data_match.btn_text', {
              defaultValue: 'Find more collaborations'
            })}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ModalSoftDeleteTip;
